import { ChangeEvent, FC, useEffect, useState } from 'react';
import CenterModal from '../../component/centerModal/centerModal';
import { Trans } from 'react-i18next';
import { renderFormRow } from './userManagementHelper';
import DropDownInput from '../../component/dropDownInput/dropDownInput';
import { useOrganizationContext } from '../../service/context/organizationContext';
import Input from '../../component/Input/input';
import { useEditVenue, useUsersSummary } from '../../hooks/useUserData';
import { EditOrgUnitProps, venueBranchOptionType } from './userManagement.type';
import {
  OrganizationUnit,
  OrganizationUnitType,
  UpdateUserResponse,
} from '@inplay/models';
import {
  apiStatusCode,
  StatusResponse,
  VenueBranch,
} from '../../utils/helper.type';
import { VenuesViewKebabOptions } from './venuesView/venuesView.type';

const EditOrgUnit: FC<EditOrgUnitProps> = (props) => {
  const { organizationSummary, fetchOrganizationSummary } =
    useOrganizationContext();
  const { fetchUsersData } = useUsersSummary();

  const [venueBranch, setVenueBranch] = useState<string>('');
  const [organizationUnitId, setOrganizationUnitId] = useState<string>('');

  const [editOrgUnitField, setEditOrgUnitField] = useState<{
    parent: string;
    orgUnitName: string;
  }>({
    parent: '',
    orgUnitName: '',
  });

  const [errorMessage, setErrorMessage] = useState({
    parent: '',
    orgUnitName: '',
  });

  const { editVenue, editVenueLoading, apiError } = useEditVenue();
  const [error, setError] = useState<string | null>(null);

  //Get option from organization summary for venue branch
  const venueBranchOption: venueBranchOptionType[] | undefined =
    organizationSummary
      ?.filter(
        (org): org is VenueBranch =>
          org.type === OrganizationUnitType.VENUE_BRANCH,
      )
      .map((organization) => ({
        label: organization.name,
        value: organization._id,
        venueId: organization.venueId,
        organizationId: organization.organizationId,
      }));

  const handleVenueFieldChange = (field: string, value: string) => {
    setEditOrgUnitField((prevVenue) => ({
      ...prevVenue,
      [field]: value,
    }));
  };

  const setFieldError = (field: string, message: string) => {
    setErrorMessage((prevError) => ({
      ...prevError,
      [field]: message,
    }));
  };

  const validateFields = (): boolean => {
    let hasError: boolean = false;

    setErrorMessage({ parent: '', orgUnitName: '' });
    setError('');
    if (!editOrgUnitField.orgUnitName.trim()) {
      setFieldError('orgUnitName', 'Venue branch name is required'); //TODO In input component added simply div for error message so i will manage in next PR
      hasError = true;
    }
    if (props.editType === undefined && !venueBranch.trim()) {
      setFieldError('parent', 'Please select venue branch');
      hasError = true;
    }
    return hasError;
  };

  const handleSave = async () => {
    const hasError: boolean = validateFields();
    if (hasError) return;

    try {
      console.log(`[ENTRY][EditVenue]`);
      const editVenueDataResponse: UpdateUserResponse = await editVenue(
        organizationUnitId,
        editOrgUnitField.orgUnitName,
      );

      const editVenueResponse: StatusResponse = {
        statusCode: apiStatusCode.SUCCESS,
        data: editVenueDataResponse,
      };
      console.log(`[EditVenue] response `, editVenueResponse);
      if (editVenueResponse.statusCode === apiStatusCode.SUCCESS) {
        fetchOrganizationSummary();
        fetchUsersData();
        props.setIsOpen?.(!props.isOpen);
        console.log(`[EditVenue] close`);
      }
    } catch (error: unknown) {
      console.error('Error editing venue:', error);
    }
  };

  const handleClear = () => {
    if (props.editType) {
      setEditOrgUnitField((prev) => {
        return { ...prev, orgUnitName: '' };
      });
    } else {
      setVenueBranch('');
      setEditOrgUnitField({
        parent: '',
        orgUnitName: '',
      });
    }
  };

  const handleVenueBranchSelect = (value: string | string[]) => {
    if (typeof value !== 'string') return;

    setVenueBranch(value);

    const selectedVenueBranch: venueBranchOptionType | undefined =
      venueBranchOption?.find(
        (venueOption: venueBranchOptionType) => venueOption.value === value,
      );

    if (selectedVenueBranch?.organizationId) {
      setOrganizationUnitId(selectedVenueBranch.value);
    }

    const childVenueId: OrganizationUnit | undefined =
      organizationSummary?.find(
        (organization: any) =>
          organization._id === selectedVenueBranch?.venueId, //TODO Getting issue so will update next PR
      );

    if (childVenueId) {
      setEditOrgUnitField((prevVenue) => ({
        ...prevVenue,
        parent: childVenueId.name,
      }));
    }

    if (errorMessage.parent) {
      setErrorMessage((prevError) => ({ ...prevError, parent: '' }));
    }
  };

  useEffect(() => {
    if (props.editType && props.data) {
      if (props.editType === VenuesViewKebabOptions.EDIT_VENUE_BRANCH) {
        const venueBranchData: VenueBranch & { _id: string } =
          props.data as VenueBranch & { _id: string };
        setVenueBranch(venueBranchData.name);
        setOrganizationUnitId(venueBranchData._id);
        setEditOrgUnitField({
          orgUnitName: venueBranchData.name,
          parent:
            organizationSummary?.filter(
              (
                org: OrganizationUnit & {
                  _id: string;
                },
              ) => org._id === venueBranchData.venueId,
            )[0].name ?? '',
        });
      } else {
        const orgData: OrganizationUnit & { _id: string } = props.data;
        setOrganizationUnitId(orgData._id);
        setEditOrgUnitField({
          orgUnitName: orgData.name,
          parent: '',
        });
      }
    }
  }, [props.editType, props.data]);

  const modalTitle: string =
    props.editType === VenuesViewKebabOptions.EDIT_VENUE_BRANCH
      ? 'userManagement.editVenueBranch'
      : props.editType === VenuesViewKebabOptions.EDIT_VENUE
        ? 'userManagement.editVenue'
        : props.editType === VenuesViewKebabOptions.EDIT_SUB_ORG
          ? 'userManagement.editSubOrg'
          : props.editType === VenuesViewKebabOptions.EDIT_ORG
            ? 'userManagement.editOrg'
            : 'userManagement.settingOption.editVenue';

  const formText: string =
    props.editType === VenuesViewKebabOptions.EDIT_VENUE
      ? 'userManagement.editVenueText'
      : props.editType === VenuesViewKebabOptions.EDIT_SUB_ORG
        ? 'userManagement.editSubOrgText'
        : 'userManagement.editOrgText';

  const inputLabel: string =
    props.editType === VenuesViewKebabOptions.EDIT_VENUE
      ? 'userManagement.venueText'
      : props.editType === VenuesViewKebabOptions.EDIT_SUB_ORG
        ? 'userManagement.subOrgText'
        : 'userManagement.orgText';

  const inputPlaceholder: string =
    props.editType === VenuesViewKebabOptions.EDIT_VENUE
      ? 'Enter venue name'
      : props.editType === VenuesViewKebabOptions.EDIT_SUB_ORG
        ? 'Enter sub-organisation name'
        : 'Enter organisation name';

  const errorMsg: string =
    props.editType === VenuesViewKebabOptions.EDIT_VENUE
      ? 'Venue name is required'
      : props.editType === VenuesViewKebabOptions.EDIT_SUB_ORG
        ? 'Sub-organisation name is required'
        : 'Organisation name is required';

  return (
    <CenterModal
      isOpen={props.isOpen}
      onClose={() => props.setIsOpen?.(!props.isOpen)}
      onClear={handleClear}
      title={modalTitle}
      onSubmit={handleSave}
      clearText={'profile.clear'}
      successText={'profile.save'}
      isLoading={editVenueLoading}
      isDisabled={editOrgUnitField.orgUnitName === ''}
    >
      {props.data ? (
        props.editType === VenuesViewKebabOptions.EDIT_VENUE_BRANCH ? (
          <>
            <div className="mb-2 text-sm text-gray-600">
              <Trans i18nKey="userManagement.editVenueBranchText" />
            </div>
            {renderFormRow(
              <Trans i18nKey="userManagement.venue" />,
              <Input disabled value={editOrgUnitField.parent} />,
            )}

            {renderFormRow(
              <Trans i18nKey="userManagement.venueBranchName" />,
              <Input
                placeholder="Enter venue branch name"
                value={editOrgUnitField.orgUnitName}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  handleVenueFieldChange('orgUnitName', value);
                  setFieldError(
                    'orgUnitName',
                    value.trim() ? '' : 'Venue branch name is required',
                  );
                }}
                errorMessage={errorMessage.orgUnitName}
              />,
            )}
          </>
        ) : (
          <>
            <div className="mb-2 text-sm text-gray-600">
              <Trans i18nKey={formText} />
            </div>
            {renderFormRow(
              <Trans i18nKey={inputLabel} />,
              <Input
                placeholder={inputPlaceholder}
                value={editOrgUnitField.orgUnitName}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  handleVenueFieldChange('orgUnitName', value);
                  setFieldError('orgUnitName', value.trim() ? '' : errorMsg);
                }}
                errorMessage={errorMessage.orgUnitName}
              />,
            )}
          </>
        )
      ) : (
        <>
          <div className="mb-2 text-sm text-gray-600">
            <Trans i18nKey="userManagement.editVenueTitle" />,
          </div>

          {renderFormRow(
            <Trans i18nKey="userManagement.venueBranch" />,
            <>
              <DropDownInput
                placeholder={<Trans i18nKey={'userManagement.select'} />}
                options={venueBranchOption}
                selected={venueBranch}
                errorMessage={errorMessage.parent}
                onSelect={handleVenueBranchSelect}
              />
            </>,
          )}

          {renderFormRow(
            <Trans i18nKey="userManagement.venue" />,
            <Input disabled value={editOrgUnitField.parent} />,
          )}

          {renderFormRow(
            <Trans i18nKey="userManagement.venueBranchName" />,
            <Input
              placeholder="Enter venue branch name"
              value={editOrgUnitField.orgUnitName}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value;
                handleVenueFieldChange('orgUnitName', value);
                setFieldError(
                  'orgUnitName',
                  value.trim() ? '' : 'Venue branch name is required',
                );
              }}
              errorMessage={errorMessage.orgUnitName}
            />,
          )}
        </>
      )}

      {(apiError || error) && (
        <div className="text-[#D80000] text-sm mt-2">{apiError || error}</div>
      )}
    </CenterModal>
  );
};

export default EditOrgUnit;
